import { Component, ElementRef, EventEmitter, OnDestroy, Output, ViewChild } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

export interface VideoRecorderData {
  safeVideoUrl: SafeUrl;
  videoBlobUrl: string;
  blob: Blob;
  mimeType: string;
}

const SUPPORTED_VIDEO_MIME_TYPES = [
  'video/mp4;codecs=h264,aac',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/ogg;codecs=theora,vorbis',
];

@Component({
  selector: 'ptl-video-recorder',
  templateUrl: './video-recorder.component.html',
  styleUrls: ['./video-recorder.component.scss'],
})
export class VideoRecorderComponent implements OnDestroy {
  @ViewChild('previewVideo') previewVideo!: ElementRef<HTMLVideoElement>;

  mediaRecorder: MediaRecorder;
  videoBlobs: Blob[] = [];
  stream: MediaStream;
  recording = false;

  videoBlob: Blob | null = null;
  videoBlobUrl: string | null = null;
  safeVideoUrl: SafeUrl | null = null;

  @Output() recordFinished = new EventEmitter<VideoRecorderData>();
  @Output() recordProcessActive = new EventEmitter<boolean>();

  constructor(private sanitizer: DomSanitizer) {}

  ngOnDestroy() {
    if (this.videoBlobUrl) {
      URL.revokeObjectURL(this.videoBlobUrl);
    }
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
    }
    this.recordProcessActive.emit(false);
  }

  private getSupportedVideoMimeType(): string {
    for (const mimeType of SUPPORTED_VIDEO_MIME_TYPES) {
      if (MediaRecorder.isTypeSupported(mimeType)) {
        return mimeType;
      }
    }
    return '';
  }

  async startRecording() {
    this.recordProcessActive.emit(true);
    this.stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });

    const videoEl = this.previewVideo.nativeElement;
    videoEl.srcObject = this.stream;
    videoEl.muted = true;
    await videoEl.play();

    const mimeType = this.getSupportedVideoMimeType();
    this.videoBlobs = [];
    this.mediaRecorder = new MediaRecorder(this.stream, mimeType ? { mimeType } : undefined);
    this.mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.videoBlobs.push(e.data);
    };
    this.mediaRecorder.start();

    this.recording = true;
  }

  async stopRecording() {
    this.recordProcessActive.emit(false);
    await new Promise((resolve) => {
      this.mediaRecorder.addEventListener('stop', () => resolve(null));
      this.mediaRecorder.stop();
    });

    this.stream.getTracks().forEach((t) => t.stop());
    this.recording = false;

    const mimeType = this.mediaRecorder.mimeType || 'video/webm';
    const rawBlob = new Blob(this.videoBlobs, { type: mimeType });

    if (this.videoBlobUrl) {
      URL.revokeObjectURL(this.videoBlobUrl);
    }

    this.videoBlob = rawBlob;
    this.videoBlobUrl = URL.createObjectURL(rawBlob);
    this.safeVideoUrl = this.sanitizer.bypassSecurityTrustUrl(this.videoBlobUrl);

    this.recordFinished.emit({
      safeVideoUrl: this.safeVideoUrl,
      videoBlobUrl: this.videoBlobUrl,
      blob: this.videoBlob,
      mimeType,
    });
  }
}
